// ABOUTME: Banner showing domain peer count for shadow IT consolidation.
// ABOUTME: Prompts users to explore team plans when colleagues use Obsidian.

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, X } from 'lucide-react';
import { userApi } from '@/services/api/client';

const DISMISS_KEY = 'domain-consolidation-dismissed';

function isLocallyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const data = JSON.parse(raw);
    return data.dismissed === true;
  } catch {
    return false;
  }
}

function setLocallyDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ dismissed: true, timestamp: Date.now() }));
  } catch {
    // localStorage not available
  }
}

export function DomainConsolidationBanner() {
  const navigate = useNavigate();
  const [peerCount, setPeerCount] = useState<number>(0);
  const [emailDomain, setEmailDomain] = useState<string>('');
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [dismissed, setDismissed] = useState<boolean>(() => isLocallyDismissed());
  const [loaded, setLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (dismissed) {
      setLoaded(true);
      return;
    }

    let cancelled = false;

    async function fetchPeers() {
      try {
        const result = await userApi.getDomainPeers();
        if (!cancelled) {
          setPeerCount(result.peer_count);
          setEmailDomain(result.email_domain);
          setShowBanner(result.show_consolidation_prompt);
          setLoaded(true);
        }
      } catch (err) {
        console.error('Failed to fetch domain peers:', err);
        if (!cancelled) {
          setLoaded(true);
        }
      }
    }

    fetchPeers();

    return () => {
      cancelled = true;
    };
  }, [dismissed]);

  const handleDismiss = async () => {
    setDismissed(true);
    setShowBanner(false);
    setLocallyDismissed();

    try {
      await userApi.dismissDomainPrompt();
    } catch (err) {
      console.error('Failed to persist domain prompt dismissal:', err);
    }
  };

  if (!loaded || !showBanner || dismissed) {
    return null;
  }

  return (
    <div data-testid="domain-consolidation-banner" className="flex items-center justify-between px-4 py-3 bg-blue-50 border border-blue-100 rounded-sm mb-4">
      <div className="flex items-center gap-3 text-sm text-blue-700">
        <Users className="w-4 h-4 flex-shrink-0" />
        <span>
          <strong>{peerCount}</strong> other people at <strong>{emailDomain}</strong> are using Obsidian BI
        </span>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/upgrade')}
          className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors whitespace-nowrap"
        >
          Explore team plans
        </button>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss domain consolidation banner"
          className="text-blue-400 hover:text-blue-600 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
